import { useEffect, useId, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAccessToken, useDistributionPrincipal } from '@dss/auth'
import { ApiError, listClientEmployees, getClientEmployee, listClientEmployeePairs,
  listClientEmployeePermissions, blockClientEmployee, restoreClientEmployee,
  replaceClientEmployeePairs, setClientEmployeePermission, type ClientEmployee,
  type ClientEmployeePairCandidates, type ClientEmployeePermission,
  type ClientEmployeePermissionEffect } from '@dss/api-client'
import { Banner, Button, Card, Input } from '@dss/design-system'
import { api } from '../../lib/api'
import { readEmployeeAttempt, type EmployeeAttempt } from './attempt'
import './client-employees.css'

export function ClientEmployeeLifecycle({ groupId, onAccessLost }: {
  groupId: number; onAccessLost: () => void;
}) {
  const token = useAccessToken()!
  const principal = useDistributionPrincipal()
  const scope = `${principal?.distributorId}:${principal?.userId}:${groupId}`
  const storageKey = `client-employee-action:${scope}`
  const [initial] = useState(() => {
    try { return { attempt: readEmployeeAttempt(storageKey), error: null } }
    catch (error) { return { attempt: null, error } }
  })
  const [attempt, setAttempt] = useState<EmployeeAttempt | null>(initial.attempt)
  const [selected, setSelected] = useState<number | null>(initial.attempt?.id ?? null)
  const [page, setPage] = useState(0)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<unknown>(initial.error)
  const [verified, setVerified] = useState(false)
  const [notice, setNotice] = useState('')
  const [draftGeneration, setDraftGeneration] = useState(0)
  const list = useQuery({ queryKey: ['client-employees', scope, page],
    queryFn: ({ signal }) => listClientEmployees(api, token, page, signal), retry: false })
  const detail = useQuery({ queryKey: ['client-employee', scope, selected],
    queryFn: ({ signal }) => getClientEmployee(api, token, selected!, signal),
    enabled: selected !== null, retry: false })
  const pairs = useQuery({ queryKey: ['client-employee-pairs', scope],
    queryFn: ({ signal }) => listClientEmployeePairs(api, token, signal), retry: false })
  const permissions = useQuery({ queryKey: ['client-employee-permissions', scope],
    queryFn: ({ signal }) => listClientEmployeePermissions(api, token, signal), retry: false })
  const denied = [list.error, detail.error, pairs.error, permissions.error, failure]
    .some(error => error instanceof ApiError && [401, 403].includes(error.status))
  useEffect(() => { if (denied) onAccessLost() }, [denied, onAccessLost])
  if (denied) return <Banner variant="danger" body="Доступ изменился. Данные сотрудников скрыты." />

  async function refresh() {
    const results = await Promise.all([detail.refetch(), pairs.refetch(), permissions.refetch(), list.refetch()])
    const failed = results.find(result => result.error)
    if (failed?.error) throw failed.error
  }
  async function change(action: string, mutate: (employee: ClientEmployee) => Promise<ClientEmployee>) {
    if (!detail.data || detail.isError || busy || attempt || initial.error) return
    const next = { id: detail.data.membership.id, version: detail.data.membership.version, action }
    try { sessionStorage.setItem(storageKey, JSON.stringify(next)) }
    catch { setFailure(new Error('Хранилище восстановления недоступно. Запрос не отправлен.')); return }
    setAttempt(next); setBusy(true); setVerified(false); setFailure(null); setNotice('')
    try {
      await mutate(detail.data)
      await refresh()
      sessionStorage.removeItem(storageKey); setAttempt(null)
      setDraftGeneration(value => value + 1)
      setNotice('Изменение подтверждено. Проверьте актуальные назначения и состояние сессий ниже.')
    } catch (error) { setFailure(error) } finally { setBusy(false) }
  }
  async function verify() {
    setBusy(true); setVerified(false); setFailure(null)
    try { await refresh(); setVerified(true); setDraftGeneration(value => value + 1) }
    catch (error) { setFailure(error) } finally { setBusy(false) }
  }
  const locked = busy || Boolean(attempt || initial.error)
  return <Card padding="lg" className="client-employees"><h2>Сотрудники моей группы</h2>
    <p>Здесь показан уже выданный доступ. Приглашения находятся ниже. Сотрудник работает только
      с назначенными парами точки и плательщика; новые пары группы ему автоматически не выдаются.</p>
    {notice && <p role="status">{notice}</p>}
    {Boolean(failure) && <Banner variant="warning" body={initial.error instanceof Error ? initial.error.message
      : failure instanceof ApiError && failure.status === 409
        ? 'Права, назначения или версия изменились. Сверьте текущее состояние перед новым действием.'
        : 'Результат не подтверждён. Не повторяем изменение автоматически; сначала прочитайте текущее состояние.'} />}
    <Button disabled={busy} onClick={() => void list.refetch()}>Обновить список сотрудников</Button>
    {list.isPending ? <p role="status">Загружаем сотрудников…</p> : list.isError
      ? <p role="alert">Список недоступен. Повторите загрузку.</p> : <>
        {!list.data.content.length && <p>Пока нет сотрудников с принятым приглашением.</p>}
        <ul className="client-employee-list">{list.data.content.map(item => <li key={item.membership.id}>
          <Button disabled={locked} aria-pressed={selected === item.membership.id} onClick={() => {
            setSelected(item.membership.id); setFailure(null); setNotice(''); setVerified(false)
          }}>{item.name ?? `Сотрудник №${item.membership.id}`}</Button>
          <p>{item.email ?? 'Email не сохранён'} · {item.membership.state === 'ACTIVE' ? 'Активен' : 'Доступ закрыт'}</p>
        </li>)}</ul>
        {(list.data.totalPages > 1 || page > 0) && <div className="client-employee-actions"><Button disabled={locked || page === 0}
          onClick={() => setPage(page - 1)}>Предыдущие сотрудники</Button>
          <span>Страница {page + 1} из {Math.max(1, list.data.totalPages)}</span>
          <Button disabled={locked || page + 1 >= list.data.totalPages}
            onClick={() => setPage(page + 1)}>Следующие сотрудники</Button></div>}
      </>}
    {attempt && <section aria-label="Проверка предыдущего действия"><h3>Проверка предыдущего действия</h3>
      <p>Сотрудник №{attempt.id}, версия {attempt.version}. Действие: {attempt.action}.</p>
      <Button disabled={busy} onClick={() => void verify()}>Прочитать текущий результат</Button>
      {verified && <p role="status">Ниже — текущее состояние сервера, а не обещание выполнения прежнего запроса.
        Сверьте права и назначения. Новый запрос не повторит предыдущий автоматически.</p>}
      <Button disabled={busy || !verified} onClick={() => {
        try {
          sessionStorage.removeItem(storageKey); setAttempt(null); setVerified(false); setFailure(null)
          setDraftGeneration(value => value + 1)
        } catch (error) { setFailure(error) }
      }}>Результат проверен — разрешить новые изменения</Button>
    </section>}
    {selected !== null && (detail.isPending ? <p role="status">Загружаем права сотрудника…</p>
      : detail.isError ? <p role="alert">Карточка недоступна. <Button disabled={busy}
        onClick={() => void detail.refetch()}>Обновить карточку</Button></p>
        : detail.data && <section aria-label="Карточка сотрудника">
          <h3>{detail.data.name ?? `Сотрудник №${selected}`}</h3>
          <details><summary>Идентификатор и версия проверки</summary>
            <p>UUID аккаунта: {detail.data.membership.subject}. Версия: {detail.data.membership.version}.</p></details>
          <p>{detail.data.membership.state === 'ACTIVE' ? 'Доступ активен.'
            : detail.data.membership.blockReason === 'CLIENT_OWNER' ? 'Заблокирован владельцем клиентской группы.'
              : 'Доступ закрыт не владельцем клиентской группы. Для восстановления обратитесь к дистрибьютору.'}</p>
          <p>{detail.data.sessionRevocationPending ? 'Завершаем прежние сессии. Доступ в портал уже закрыт.'
            : 'Незавершённых заданий закрытия сессий нет.'}</p>
          <Button disabled={locked} onClick={() => void verify()}>Обновить карточку и назначения</Button>
          <div className="client-employee-actions"><Button
            disabled={locked || detail.data.membership.state !== 'ACTIVE'} onClick={() => {
              if (!window.confirm('Закрыть доступ сотруднику и завершить текущие сессии? Заказы и история сохранятся.')) return
              void change('Блокировка доступа', item => blockClientEmployee(api, token, item.membership.id, item.membership.version))
            }}>Заблокировать сотрудника</Button>
            <Button disabled={locked || detail.data.membership.state !== 'BLOCKED'
              || detail.data.membership.blockReason !== 'CLIENT_OWNER' || detail.data.sessionRevocationPending
              || !detail.data.effectivePairs.length
              || detail.data.assignedPairs.length !== detail.data.effectivePairs.length} onClick={() => {
                if (!window.confirm('Восстановить доступ с указанными ниже правами и назначениями?')) return
                void change('Восстановление доступа', item => restoreClientEmployee(api, token, item.membership.id, item.membership.version))
              }}>Восстановить сотрудника</Button></div>
          {pairs.isPending ? <p>Читаем разрешённые пары группы…</p> : pairs.isError
            ? <p role="alert">Назначения недоступны. Обновите карточку и назначения.</p>
            : <PairEditor key={`${selected}:${detail.data.membership.version}:${pairs.data.groupVersion}:${draftGeneration}`}
              employee={detail.data} candidates={pairs.data} locked={locked}
              onSave={selectedPairs => void change('Изменение назначенных пар', item => replaceClientEmployeePairs(api,
                token, item.membership.id, item.membership.version, pairs.data.groupVersion, selectedPairs))} />}
          <h4>Индивидуальные действия</h4><p>Разрешение действия не расширяет список торговых точек.
            Личный запрет имеет приоритет над базовым шаблоном.</p>
          {permissions.isError ? <p role="alert">Каталог действий недоступен.</p>
            : permissions.data?.map(permission => <PermissionEditor
              key={`${selected}:${detail.data!.membership.version}:${permission.code}:${draftGeneration}`}
              employee={detail.data!} permission={permission} locked={locked}
              onSave={effect => void change(`Право ${permission.code}: ${effect}`, item => setClientEmployeePermission(api,
                token, item.membership.id, item.membership.version, permission.code, effect))} />)}
        </section>)}
  </Card>
}

const pairKey = (pair: { tradepointId: number; payerId: number }) => `${pair.tradepointId}:${pair.payerId}`
function PairEditor({ employee, candidates, locked, onSave }: { employee: ClientEmployee;
  candidates: ClientEmployeePairCandidates; locked: boolean;
  onSave: (pairs: Array<{ tradepointId: number; payerId: number }>) => void }) {
  const [selected, setSelected] = useState(employee.assignedPairs.map(pairKey))
  const [search, setSearch] = useState('')
  const searchId = useId()
  const available = new Set(candidates.pairs.map(pairKey))
  const missing = selected.filter(key => !available.has(key))
  const visible = candidates.pairs.filter(pair => `${pair.tradepointName} ${pair.address ?? ''} ${pair.payerName} ${pair.inn ?? ''}`
    .toLocaleLowerCase('ru-RU').includes(search.trim().toLocaleLowerCase('ru-RU')))
  return <form onSubmit={event => { event.preventDefault();
    if (!locked && !missing.length) onSave(candidates.pairs.filter(pair => selected.includes(pairKey(pair)))
      .map(({ tradepointId, payerId }) => ({ tradepointId, payerId })))
  }}><fieldset disabled={locked}><legend>Назначенные точки и плательщики</legend>
    <p>Валидных назначений: {employee.effectivePairs.length}. Выбрано: {selected.length}.
      При блокировке эти назначения не дают доступ до явного восстановления.
      Пустой выбор убирает бизнес-доступ, но не блокирует аккаунт.</p>
    <label htmlFor={searchId}>Поиск точки, адреса или плательщика<Input id={searchId} name="pair-search" value={search}
      onChange={event => setSearch(event.target.value)} /></label>
    {missing.length > 0 && <p role="alert">Прежних пар больше нет в доступном наборе: {missing.join(', ')}.
      <Button onClick={() => setSelected(selected.filter(key => available.has(key)))}>Убрать недоступные назначения</Button></p>}
    <div className="client-employee-pairs">{visible.map(pair => <label key={pairKey(pair)}>
      <input name="assigned-pairs" type="checkbox" checked={selected.includes(pairKey(pair))}
        onChange={event => setSelected(event.target.checked ? [...selected, pairKey(pair)]
          : selected.filter(key => key !== pairKey(pair)))} />
      <span>{pair.tradepointName} · {pair.address ?? 'Адрес не указан'} — {pair.payerName}
        <small>ИНН {pair.inn ?? 'не указан'} · точка №{pair.tradepointId}, плательщик №{pair.payerId}</small></span>
    </label>)}</div>
    {!visible.length && <p>Подходящих пар нет.</p>}
    <Button type="submit" disabled={missing.length > 0}>Сохранить назначения</Button>
  </fieldset></form>
}

function PermissionEditor({ employee, permission, locked, onSave }: { employee: ClientEmployee;
  permission: ClientEmployeePermission; locked: boolean; onSave: (effect: ClientEmployeePermissionEffect) => void }) {
  const [effect, setEffect] = useState<ClientEmployeePermissionEffect>(
    employee.overrides.find(item => item.code === permission.code)?.effect ?? 'INHERIT')
  const wouldGrant = effect === 'GRANT' || effect === 'INHERIT' && employee.membership.basePermissions.includes(permission.code)
  return <form onSubmit={event => { event.preventDefault(); if (!locked && (!wouldGrant || permission.canGrant)) onSave(effect) }}>
    <fieldset disabled={locked}><legend>{permission.label}</legend>
      <p>Сейчас: {employee.effectivePermissions.includes(permission.code) ? 'разрешено' : 'недоступно'}.
        Базовый шаблон: {employee.membership.basePermissions.includes(permission.code) ? 'разрешено' : 'не выдано'}.</p>
      {(['INHERIT', 'GRANT', 'DENY'] as const).map(value => <label className="client-employee-choice" key={value}>
        <input type="radio" name={`permission-${permission.code}`} checked={effect === value}
          onChange={() => setEffect(value)} />
        {value === 'INHERIT' ? 'По базовому шаблону' : value === 'GRANT' ? 'Разрешить лично' : 'Запретить лично'}
      </label>)}
      {!permission.canGrant && <p>У вас нет права делегировать это действие. Разрешающее изменение недоступно.</p>}
      <Button type="submit" disabled={wouldGrant && !permission.canGrant}>Сохранить: {permission.label}</Button>
    </fieldset>
  </form>
}
